import { useEffect, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSharedValidations, youtubeRegex, urlRegex } from '../../../../hooks/useSharedValidations';
import { useParams, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Col, Row, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useProfileCreationFormContext } from '../../ProfileCreationFormProvider';
import { incrementCurrentStep, triggerChangePercentage } from '../../../../store/slices/sellerProfileSlice';
import {
  useGetCompanyInfoQuery,
  useGetProfileInfoQuery,
  useUpdateProfileInfoMutation,
  useGetCompanySizesQuery,
  useGetLanguagesQuery,
  useUploadPhotoMutation,
  useGetProfilePhotoQuery,
} from '../../../../store/services/sellerProfileCreationService';

import TitleText from '../../../atoms/TitleText';
import InfoText from '../../../atoms/InfoText';
import FormField from '../../../FormField/FormField';
import Button from '../../../atoms/Button';
import Languages from './Languages';
import dayjs from 'dayjs';
import HelperText from '../../../atoms/HelperText';

const CompanyInfo = ({ isReview = false, disableSubmitRegistration = false, onSuccess }: { isReview?: boolean; disableSubmitRegistration?: boolean; onSuccess?: () => void }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [hasInitialized, setHasInitialized] = useState(false);
  const [languageError, setLanguageError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { registerSubmit } = useProfileCreationFormContext();

  const { sellerId } = useParams<{ sellerId: string }>();
  const [searchParams] = useSearchParams();
  const isEditMode = searchParams.get('mode') === 'edit' && !disableSubmitRegistration;
  const profileId = sellerId || '';

  // Stub queries
  const { data: profileInfoData, refetch: refetchCompanyInfo } = useGetCompanyInfoQuery({ profileId }, { skip: !profileId });
  const { refetch: refetchProfileInfo } = useGetProfileInfoQuery({ profileId }, { skip: !profileId });
  const { data: profilePhotoData } = useGetProfilePhotoQuery({ profileId }, { skip: !profileId });
  const { data: companySizeData } = useGetCompanySizesQuery();
  const { data: languagesData, refetch: refetchLanguages } = useGetLanguagesQuery({ profileId });
  const [updateProfileInfo] = useUpdateProfileInfoMutation();
  const [uploadPhoto] = useUploadPhotoMutation();

  const { createDescriptionSchemaFun } = useSharedValidations();
  const required_error = t('components.validations.requiredField');

  const schema = z.object({
    imageFile: z.custom<any>(
      (file) => {
        if (!file) return false;
        return true;
      },
      { message: required_error }
    ),
    companySize: z.string({ message: required_error }),

    foundationYear: z.custom<dayjs.Dayjs>((val) => dayjs.isDayjs(val), {
      message: required_error,
    }),
    CompanyBio: createDescriptionSchemaFun(20, 1000),
    introUrl: z
      .string()
      .superRefine((val, ctx) => {
        const trimmed = val?.trim();

        if (!!trimmed && !youtubeRegex.test(trimmed)) {
          ctx.addIssue({
            code: 'custom',
            message: t('components.invalidURL'),
          });
        }
      })
      .optional()
      .nullable(),

    websiteLink: z
      .string()
      .superRefine((val, ctx) => {
        const trimmed = val?.trim();

        if (!!trimmed && !urlRegex.test(trimmed)) {
          ctx.addIssue({
            code: 'custom',
            message: t('components.invalidURL'),
          });
        }
      })
      .optional()
      .nullable(),
  });

  type FormDataType = z.infer<typeof schema>;

  const method = useForm<FormDataType>({
    resolver: zodResolver(schema),
    mode: 'all',
    defaultValues: {
      CompanyBio: '',
      companySize: undefined as string | undefined,
      introUrl: '',
      foundationYear: undefined as any,
      websiteLink: '',
      imageFile: null as any,
    },
  });

  useEffect(() => {
    if (!hasInitialized && profileInfoData?.payload && languagesData?.payload && companySizeData?.payload) {
      const companySizeId = profileInfoData.payload.companySize?.id?.toString() || undefined;

      method.reset({
        CompanyBio: profileInfoData.payload.bio || '',
        companySize: companySizeId,
        introUrl: profileInfoData.payload.introductionVideoUrl || '',
        foundationYear: profileInfoData.payload.foundedYear ? dayjs(String(profileInfoData.payload.foundedYear), 'YYYY') : undefined,
        websiteLink: profileInfoData.payload.websiteLink || '',
        imageFile: profileInfoData.payload.photo?.blobFileUrl || null,
      });
      setHasInitialized(true);
    }
  }, [profileInfoData, languagesData, companySizeData, method, hasInitialized]);

  useEffect(() => {
    if (hasInitialized && languagesData?.payload?.length) {
      setLanguageError(null);
    }
  }, [hasInitialized, languagesData]);


  const submitForm = async (dataForm: FormDataType) => {
    try {
      setIsSubmitting(true);
      if (!languagesData?.payload?.length) {
        setLanguageError(required_error);
        message.error(t('components.languageRequired'));
        return;
      }
      setLanguageError(null);

      let photoId = profilePhotoData?.payload?.id || profileInfoData?.payload?.photo?.id;
      const imageFile = dataForm.imageFile;
      if (imageFile && typeof imageFile !== 'string') {
        const formData = new FormData();
        const fileObj = imageFile.originFileObj || imageFile;
        formData.append('files', fileObj);
        const uploadResponse = await uploadPhoto(formData).unwrap();
        photoId = uploadResponse.payload?.[0]?.id;
      }

      const payload = {
        bio: dataForm.CompanyBio,
        ...(dataForm.introUrl?.trim() ? { introductionVideoUrl: dataForm.introUrl.trim() } : {}),
        ...(dataForm.websiteLink?.trim() ? { websiteLink: dataForm.websiteLink.trim() } : {}),
        ...(dataForm.companySize ? { companySizeId: dataForm.companySize } : {}),
        ...(dataForm.foundationYear ? { foundedYear: dayjs(dataForm.foundationYear).year() } : {}),
        ...(photoId ? { fileId: photoId } : {}),
      };
      await updateProfileInfo({
        profileId,
        data: payload,
      }).unwrap();

      if (profileId) {
        await Promise.all([refetchCompanyInfo(), refetchProfileInfo()]);
      }

      onSuccess?.();
      if (!isReview) {
        dispatch(incrementCurrentStep());
      }
      dispatch(triggerChangePercentage());
    } catch (err: unknown) {
      const error = err as { data?: { message?: string; errors?: Record<string, string> } };
      if (error?.data?.errors) {
        Object.entries(error.data.errors).forEach(([field, msg]) => {
          const fieldMap: Record<string, string> = {
            websiteLink: 'websiteLink',
            introductionVideoUrl: 'introUrl',
            bio: 'CompanyBio',
            companySizeId: 'companySize',
            foundedYear: 'foundationYear',
          };
          const rhfField = fieldMap[field] || field;
          // @ts-expect-error dynamic field name
          method.setError(rhfField, { type: 'server', message: msg });
        });
        const firstError = Object.values(error.data.errors)[0];
        if (firstError) message.error(firstError);
      } else {
        message.error(error?.data?.message || t('components.somethingWentWrong'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleButtonClick = () => {
    const inputElement = document.getElementById('imageFile') as HTMLInputElement | null;
    if (inputElement) {
      inputElement.click();
    }
  };

  useEffect(() => {
    if (!disableSubmitRegistration) {
      registerSubmit(method.handleSubmit(submitForm));
    }
  }, [method.handleSubmit, submitForm, registerSubmit, disableSubmitRegistration]);

  return (
    <div data-testid="profile-creation-company-info-wrapper" className="mb-6">
      <div
        className="flex flex-col gap-6"
        data-testid="profile-creation-company-info-title-section"
      >
        {!isReview && (
          <div
            className="flex flex-col gap-1"
            data-testid="profile-creation-company-info-header"
          >
            <TitleText
              text={t('components.personalInformation')}
              size="2xl"
              className="font-semibold"
              data-testid="profile-creation-company-info-title"
            />
            <InfoText
              text={t('components.personalInfoDesc')}
              data-testid="profile-creation-company-info-subtitle"
            />
          </div>
        )}
      </div>

      <FormProvider {...method}>
        <form
          className="mb-6"
          onSubmit={method.handleSubmit(submitForm)}
          data-testid="profile-creation-company-info-form"
        >
          <div
            className="flex flex-col items-center gap-4 my-3 md:flex-row"
            data-testid="profile-creation-company-info-photo-upload-area"
          >
            <div data-testid="profile-creation-company-info-avatar-wrapper">
              <FormField
                type="uploadImage"
                name="imageFile"
                id="imageFile"
                maxFileSize={5}
                allowedTypes={['image/jpeg', 'image/png', 'image/jpg']}
                disabled={isEditMode}
              />
            </div>

            <div
              className="flex flex-col items-start gap-2"
              data-testid="profile-creation-company-info-photo-info"
            >
              <TitleText
                size="base"
                text={t('components.profile_photo')}
                className="font-medium"
                data-testid="profile-creation-company-info-photo-label"
              />
              <InfoText
                text={t('components.choose_photo_upload')}
                data-testid="profile-creation-company-info-photo-hint"
              />
              <Button
                type="button"
                onClick={handleButtonClick}
                disabled={isEditMode}
                variant="outline"
                data-testid="profile-creation-company-info-upload-button"
              >
                {t('components.upload_photo')}
              </Button>
            </div>
          </div>

          <Row gutter={[20, 24]}>
            <Col xs={24} md={12}>
              <FormField
                type="select"
                name="companySize"
                id="companySize"
                label={t('components.company_size')}
                placeholder={t('components.company_size')}
                labelClassName="font-semibold text-black dark:text-white"
                options={companySizeData?.payload?.map((item: any) => ({
                  label: item.size,
                  value: item.id?.toString(),
                })) || []}
                disabled={isEditMode}
              />
            </Col>
            <Col xs={24} md={12}>
              <FormField
                type="date"
                name="foundationYear"
                id="foundationYear"
                label={t('components.foundation_year')}
                placeholder={t('components.foundation_year')}
                picker="year"
                format="YYYY"
                labelClassName="font-semibold text-black dark:text-white"
                disabled={isEditMode}
              />
            </Col>
            <Col span={24}>
              <FormField
                type="textarea"
                name="CompanyBio"
                id="CompanyBio"
                label={t('components.company_bio')}
                placeholder={t('components.company_bio')}
                className="!h-[200px]"
                labelClassName="font-semibold text-black dark:text-white"
                maxLength={1000}
                helperText={t('components.textareaMinAndMaxLength', { min: 20, max: 1000 })}
                disabled={isEditMode}
              />
            </Col>
            <Col xs={24} data-testid="profile-creation-company-info-intro-col">
              <FormField
                type="text"
                name="introUrl"
                id="introUrl"
                label={
                  <span data-testid="profile-creation-company-info-intro-label">
                    <span className="font-semibold text-light-text-primary dark:text-white">
                      {t('components.introduction_video')}{' '}
                    </span>
                    <span className="text-light-text-placeholder dark:text-dark-text-placeholder font-normal text-sm">
                      {t('components.youtubeLinkDesc')}
                    </span>
                  </span>
                }
                optional
                placeholder={t('components.video_url_placeholder')}
                data-testid="profile-creation-company-info-intro-url-input"
                disabled={isEditMode}
              />
              <InfoText text={t('components.show_reel')} className="mt-1" data-testid="profile-creation-company-info-show-reel-hint" />
            </Col>
            <Col xs={24} data-testid="profile-creation-company-info-website-col">
              <FormField
                type="text"
                name="websiteLink"
                id="websiteLink"
                label={t('components.website_link')}
                optional
                placeholder={t('components.website_link')}
                data-testid="profile-creation-company-info-website-link-input"
                disabled={isEditMode}
              />
            </Col>
          </Row>

          {isReview && disableSubmitRegistration && (
            <div className="mt-6 flex justify-end">
              <Button
                type="submit"
                loading={isSubmitting}
                className="min-w-[120px]"
                data-testid="profile-creation-company-info-done-button"
              >
                {t('components.done')}
              </Button>
            </div>
          )}
        </form>

        {!disableSubmitRegistration && (
          <div className="mt-8" data-testid="profile-creation-company-info-languages">
            <Languages
              getLangs={refetchLanguages}
              langs={languagesData?.payload || []}
            />
            {languageError && (
              <div className="mt-2" data-testid="profile-creation-company-info-languages-error">
                <HelperText
                  text={languageError}
                  status="error"
                />
              </div>
            )}
          </div>
        )}
      </FormProvider>
    </div>
  );
};

export default CompanyInfo;
